import * as ko from "knockout";
import * as _ from "underscore";
import * as moment from "moment";
import * as globalConfig from "globalConfig";
import * as koMapping from "knockout.mapping";
import * as localisationHelper from 'localisationHelper';

// Helpers
import "knockoutCustomBindings";

// Models
import { FacilityModel } from 'Content/ts/Models/Join/FacilityModel';
import { KoAddressModel } from 'Content/ts/Models/Shared/AddressModels';
import { JoinConfiguration } from 'Content/ts/Models/Join/JoinConfiguration';
import { DispatchOptions } from "Content/ts/Models/Shared/Signals/signalsModels";
import { HomeClubConfigurationResponse } from "Content/ts/Models/Shared/ClubSelector/clubConfiguration";
import { IClubSelectionCustomMessages } from "Content/ts/Models/Shared/ClubSelector/ClubSelectionCustomMessages";
import { FacilityHierarchyModel } from 'Content/ts/Models/Join/FacilityHierarchyModel';

// Configuration
import { ClubSelectionConfiguration } from "Content/ts/Shared/ClubSelector/Configuration/clubSelectionConfiguration";

// Services
import { JoinService } from 'Content/ts/Join/Services/JoinService';
import { KoGoogleMap } from 'Content/ts/Models/Shared/GoogleMapsModels';
import { SignalsService } from "Content/ts/Shared/Services/Signals/signalsService";
import { ClubSelectionService } from "Content/ts/Shared/ClubSelector/Services/clubSelectionServices";

// Components
import "loading";
import "Content/ts/Shared/GoogleMaps/google-maps";
import "Content/ts/Shared/Address/address-helper";
import 'Content/ts/Shared/ClubSelector/single-club-selection';

export const Deps = {
    JoinService: JoinService,
    SignalsService: SignalsService,
    LocalisationHelper: localisationHelper
}

export interface FacilitySelectionParams {
    JoinConfig: JoinConfiguration;
}

export default class FacilitySelection {
    Configuration: JoinConfiguration;
    selectedFacility: KnockoutObservable<number>;
    journeyFacilitiesUrl: string;
    clubSelectionConfiguration: ClubSelectionConfiguration;

    facilityDetails: KnockoutObservable<FacilityModel>;
    addressDetails: KnockoutObservable<KoAddressModel>;
    facilitiesAssigned: KnockoutObservable<boolean>;

    Loading: KnockoutObservable<boolean>;
    hasGoogleMapDetails: KnockoutObservable<boolean>;
    showSingleLineAddress: KnockoutObservable<boolean>;

    googleMapDetails: KoGoogleMap;

    showGoogleMap: KnockoutComputed<boolean>;


    clubCustomMessages: IClubSelectionCustomMessages;
        
    customMessages: {
        ClubLabel: string;
        ClubPanelHeaderText: string;
    };

    constructor(params: FacilitySelectionParams) {
        // Provided Module Properties
        this.Configuration = params.JoinConfig;

        // Standard Module Properties
        this.addressDetails = ko.observable(null);
        this.facilityDetails = ko.observable(null);
        this.selectedFacility = ko.observable(null);

        this.Loading = this.Configuration.facilityLoading;
        this.facilitiesAssigned = this.Configuration.facilitiesAssigned;
        this.hasGoogleMapDetails = ko.observable(false);
        this.showSingleLineAddress = ko.observable(true);

        this.googleMapDetails = null;
        this.journeyFacilitiesUrl = `${globalConfig.Urls.Join.JourneyLocation}${this.Configuration.journeyId}`;

        this.clubCustomMessages = null;

        // Initialisation Methods
        this.retrieveCustomMessages();
        this.ComputedMethods();
        this.SubscribeMethods();
        this.InitialiseSignals();

        this.RetrieveJourneyConfiguration();
    };

    private RetrieveFacilityDetails(facilityId?: number): void {
        if (!facilityId) {
            return;
        }

        this.Loading(true);

        Deps.JoinService.getFacilityDetails(facilityId)
            .then((facilityDetails: FacilityModel) => {
                (<any>this.addressDetails) = koMapping.fromJS(facilityDetails.Address);
                this.googleMapDetails = koMapping.fromJS(facilityDetails.GoogleMap);

                if (this.googleMapDetails != null) {
                    if (this.googleMapDetails.Longitude() != 0 &&
                        this.googleMapDetails.Latitude() != 0 &&
                        this.googleMapDetails.ApiKey().length > 0) {
                        this.hasGoogleMapDetails(true);
                    }
                }

                this.facilityDetails(facilityDetails);
                this.Configuration.facilityName(facilityDetails.DisplayName);
                this.Configuration.facilityMainLocationId(facilityDetails.MainLocationId);

                var earliestMembershipStartDate = facilityDetails.EarliestMembershipStartDate
                    ? moment(facilityDetails.EarliestMembershipStartDate)
                    : moment().startOf('day');

                this.Configuration.facilityEarliestStartDate(earliestMembershipStartDate);
                this.Configuration.membershipStartDate(earliestMembershipStartDate);
            }).finally(() => {
                this.Configuration.facilitySelected.dispatch(facilityId);
                this.Loading(false);
            });
    };

    private retrieveCustomMessages() {
        this.customMessages = {
            ClubLabel: this.Configuration.customMessages.ClubLabel,
            ClubPanelHeaderText: this.Configuration.customMessages.ClubPanelHeaderText
        };

        this.clubCustomMessages = {
            ClubHelpText: this.Configuration.customMessages.ClubHelpText,
            LoadingClubs: this.Configuration.customMessages.LoadingClubs,
            ClubRequiredValidationMessage: this.Configuration.customMessages.ClubRequiredValidationMessage,
            ClubLabel: this.Configuration.customMessages.ClubLabel
        } as IClubSelectionCustomMessages;
    };

    private ComputedMethods(): void {
        this.showGoogleMap = ko.computed(() => {
            return !this.Loading()
                && this.hasGoogleMapDetails();
        });
    };

    private SubscribeMethods(): void {
        this.selectedFacility.subscribe((facilityId?: number) => {
            this.RetrieveFacilityDetails(facilityId);
        });
    };

    private RetrieveDefaultClub(): Q.Promise<HomeClubConfigurationResponse> {
        return ClubSelectionService.RetrieveDefaultClub()
            .then((response: HomeClubConfigurationResponse) => {
                return response;
            });
    }

    private InitialiseSignals(): void {
        this.clubSelectionConfiguration = new ClubSelectionConfiguration();
    };

    private RetrieveJourneyConfiguration(): void {
        this.Loading(true);

        Deps.JoinService.getJourneyLocations(this.Configuration.journeyId)
            .then((result: FacilityHierarchyModel[]) => {
                // do we have any journey allocations?
                this.facilitiesAssigned(result && result.length > 0);

                if (!this.facilitiesAssigned()) {
                    this.RetrieveDefaultClub().then((defaultClub: HomeClubConfigurationResponse) => {
                        if (defaultClub.ClubId) {
                            // hide facility selection
                            this.selectedFacility(defaultClub.ClubId);
                            this.Configuration.facilitySelected.dispatch(defaultClub.ClubId);
                        }
                        else {
                            // hide facility selection
                            this.Configuration.facilitySelected.dispatch(null);
                        }
                    });
                }
                else {
                    // if we've passed through a selected club
                    if (this.Configuration.facilityId() !== null && this.Configuration.facilityId() !== undefined) {
                        var facilityIds = _.chain(result).map((club: FacilityHierarchyModel) => {
                            // if the item in the array has children, go through each child item and pluck those
                            if (club.Children && club.Children.length > 0) {
                                var childFacilityIds = _.pluck(club.Children, "FacilityId");
                                childFacilityIds.push(club.FacilityId);
                                return childFacilityIds;
                            }
                            return club.FacilityId;
                        }).flatten().value();

                        // check if the facility passed through exists, if it does - set it as the selected
                        var clubExists = _.contains(facilityIds, this.Configuration.facilityId());
                        if (clubExists) {
                            this.selectedFacility(this.Configuration.facilityId());
                        }
                    } else {
                        // get the default location for the journey and set is as selected
                        Deps.JoinService.getJourneyDefaultLocation(this.Configuration.journeyId)
                            .then((clubId: number) => {
                                if (clubId) {
                                    this.selectedFacility(clubId);
                                }
                            })
                            .catch(() => {
                                this.Loading(false);
                            });
                    }

                    // load available facilities
                    const dispatchRequest = { ExpectedTotalListeners: 1 } as DispatchOptions;
                    Deps.SignalsService.RecursiveDispatch(this.clubSelectionConfiguration.configureClubHierarchy, dispatchRequest);
                }

                this.Loading(false);
            })
            .catch(() => {
                this.Loading(false);
            });
    };
}

const moduleName = "facility-selection";
ko.components.unregister(moduleName);
ko.components.register(moduleName, {
    viewModel: FacilitySelection,
    template: { require: `text!/Themes/Default/Content/ts/Join/${moduleName}.html` }
});